<template>
  <DashboardContainer>
    <GivarooSpinner v-if="loading" class="mt-24" />
    <template v-else>
      <!-- Toolbar (admins/staff) -->
      <div v-if="canSeeDetails" class="mb-5 list-toolbar">
        <BaseButton variant="primary" :icon="ArrowDownTrayIcon" @click="exportLeads">
          Export Leads
        </BaseButton>
        <div class="list-toolbar__right">
          <BaseButton
            variant="secondary"
            :icon="FunnelIcon"
            @click="showFilterDrawer = !showFilterDrawer"
          >
            Filters
            <span
              v-if="activeFilterCount"
              class="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-white"
            >
              {{ activeFilterCount }}
            </span>
          </BaseButton>
        </div>
      </div>

      <!-- Inline filters -->
      <FilterDrawer
        v-if="canSeeDetails"
        :open="showFilterDrawer"
        title="Filter Leads"
        subtitle="Refine the lead list"
        @close="showFilterDrawer = false"
      >
        <div class="filter-fields">
          <div class="filter-field">
            <label class="filter-field-label">Date range</label>
            <div class="flex items-center gap-2">
              <input
                name="start"
                type="date"
                class="input--text--small"
                placeholder="Select date start"
                @input="getDateRangeOption1($event.target.value)"
              />
              <span class="text-sm text-gray-500">to</span>
              <input
                name="end"
                type="date"
                class="input--text--small"
                placeholder="Select date end"
                @input="getDateRangeOption2($event.target.value)"
              />
            </div>
          </div>
          <div class="filter-field">
            <label class="filter-field-label">Campaign</label>
            <select class="input--text--small" @change="filterByCampaign($event.target.value)">
              <option :selected="!selectedCampaignFilter.id" :value="''">All Campaigns</option>
              <option
                v-for="campaign in campaignFilters"
                :key="campaignFilters.indexOf(campaign)"
                :value="campaign.id"
              >
                {{ campaign.location ? campaign.name + ' ' + campaign.location : campaign.name }}
              </option>
            </select>
          </div>
          <div class="filter-field">
            <label class="filter-field-label">Fundraiser</label>
            <select class="input--text--small" @change="filterByFundraiser($event.target.value)">
              <option :selected="!selectedFundraiserFilter.id" value="">All Fundraisers</option>
              <option
                v-for="fundraiser in fundraiserFilters"
                :key="fundraiserFilters.indexOf(fundraiser)"
                :value="fundraiser.id"
              >
                {{ fundraiser.fullName }}
              </option>
            </select>
          </div>
          <div
            v-if="selectedCampaignFilter.name || selectedFundraiserFilter.name"
            class="filter-field space-y-2 sm:col-span-2 xl:col-span-3"
          >
            <p class="text-xs font-medium uppercase tracking-wide text-gray-400">Active filters</p>
            <div class="flex flex-wrap gap-2">
              <button
                v-if="selectedCampaignFilter.name"
                type="button"
                class="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primaryDark"
                @click="clearCampaignFilter()"
              >
                {{
                  selectedCampaignFilter.location
                    ? selectedCampaignFilter.name + ' ' + selectedCampaignFilter.location
                    : selectedCampaignFilter.name
                }}
                <XMarkIcon class="h-4 w-4" />
              </button>
              <button
                v-if="selectedFundraiserFilter.name"
                type="button"
                class="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primaryDark"
                @click="clearFundraiserFilter()"
              >
                {{ selectedFundraiserFilter.name }}
                <XMarkIcon class="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        <template #footer>
          <BaseButton variant="primary" @click="showFilterDrawer = false">Done</BaseButton>
        </template>
      </FilterDrawer>

      <!-- Lead list (mobile cards) -->
      <div class="sm:hidden">
        <Spinner v-if="updatingTable" class="mt-8" />
        <template v-else>
          <ul v-if="leads.list?.length" role="list" class="mt-2 space-y-3">
            <li
              v-for="(lead, key) in leads.list"
              :key="key"
              class="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="truncate text-sm font-semibold text-gray-900">
                    {{ lead.firstName }} {{ lead.lastName }}
                  </p>
                  <p class="truncate text-xs italic text-gray-500">
                    {{
                      lead.campaign.location
                        ? lead.campaign.name + ' ' + lead.campaign.location
                        : lead.campaign.name
                    }}
                  </p>
                </div>
                <span class="shrink-0 text-xs text-gray-500">
                  {{ formatDateString(lead.datetimeCreated) }}
                </span>
              </div>
            </li>
          </ul>
          <p
            v-else
            class="mt-2 rounded-xl bg-white p-6 text-center text-sm text-gray-500 ring-1 ring-gray-200"
          >
            No results found.
          </p>
        </template>
      </div>

      <!-- Lead list (desktop table) -->
      <div class="hidden sm:block">
        <BaseTable
          :columns="leadColumns"
          :rows="leads.list"
          :loading="updatingTable"
          empty-text="No results found."
        >
          <template #cell-name="{ row }">
            <span class="font-medium text-gray-900">{{ row.firstName }} {{ row.lastName }}</span>
          </template>
          <template #cell-email="{ row }">{{ row.email }}</template>
          <template #cell-campaign="{ row }">
            {{
              row.campaign.location
                ? row.campaign.name + ' ' + row.campaign.location
                : row.campaign.name
            }}
          </template>
          <template #cell-fundraiser="{ row }">{{ row.fundraiser.fullName }}</template>
          <template #cell-date="{ row }">{{ formatDateString(row.datetimeCreated) }}</template>
        </BaseTable>
      </div>
    </template>
  </DashboardContainer>
</template>

<script>
import { computed, onBeforeMount, reactive, ref, triggerRef } from 'vue'
import { useStore } from 'vuex'
import DashboardContainer from '@/components/DashboardContainer.vue'
import Spinner from '@/components/Spinner'
import { leadFunctions, LeadApi } from '@/services/leads/'
import { XMarkIcon, FunnelIcon, ArrowDownTrayIcon } from '@heroicons/vue/24/outline'
import GivarooSpinner from '@/components/GivarooSpinner.vue'

export default {
  name: 'LeadList',
  components: {
    DashboardContainer,
    Spinner,
    XMarkIcon,
    GivarooSpinner,
  },
  setup() {
    const store = useStore()
    const isGivarooStaff = ref(store.getters.isGivarooStaff)
    const isNPOAdmin = ref(store.getters.isNPOAdmin)
    const isCanvassingOrgAdmin = ref(store.getters.isCanvassingOrgAdmin)
    const isCanvassingOrgFieldDirector = ref(store.getters.isCanvassingOrgFieldDirector)
    const { leadCollection, leadFilters } = leadFunctions()
    const leads = ref(leadCollection)
    const leadRefs = ref([])
    const loading = ref(false)
    const updatingTable = ref(false)

    const campaignFilters = computed(() =>
      leadRefs.value ? getUniqueObjectsArray(leadRefs.value, 'campaign') : null,
    )
    const fundraiserFilters = computed(() =>
      leadRefs.value ? getUniqueObjectsArray(leadRefs.value, 'fundraiser') : null,
    )

    const selectedCampaignFilter = reactive({
      location: '',
      name: '',
      id: '',
    })
    const selectedFundraiserFilter = reactive({
      name: '',
      id: '',
    })
    const selectedDateRange = reactive({
      date1: '',
      date2: '',
    })

    // Modern UI: filter drawer + role-aware table columns (presentation only)
    const showFilterDrawer = ref(false)
    const canSeeDetails = computed(
      () =>
        isGivarooStaff.value ||
        isNPOAdmin.value ||
        isCanvassingOrgAdmin.value ||
        isCanvassingOrgFieldDirector.value,
    )
    const activeFilterCount = computed(
      () =>
        (selectedCampaignFilter.id ? 1 : 0) +
        (selectedFundraiserFilter.id ? 1 : 0) +
        (selectedDateRange.date1 && selectedDateRange.date2 ? 1 : 0),
    )
    const leadColumns = computed(() => [
      { key: 'name', label: 'Name', minWidth: '10rem' },
      ...(canSeeDetails.value ? [{ key: 'email', label: 'Email', minWidth: '12rem' }] : []),
      { key: 'campaign', label: 'Campaign', minWidth: '10rem' },
      ...(canSeeDetails.value
        ? [{ key: 'fundraiser', label: 'Fundraiser', minWidth: '10rem' }]
        : []),
      { key: 'date', label: 'Date', minWidth: '8rem' },
    ])

    const getUniqueObjectsArray = (arr, key, value = false) => {
      const filteredArr = arr.filter((obj) => obj[`${key}`] != null)

      if (value) {
        return Object.values(
          filteredArr
            .map((obj) => obj[`${key}`])
            .reduce((acc, obj) => ({ ...acc, [obj.value]: obj.value }), {}),
        )
      } else {
        return Object.values(
          filteredArr
            .map((obj) => obj[`${key}`])
            .reduce((acc, obj) => ({ ...acc, [obj.id]: obj }), {}),
        )
      }
    }

    onBeforeMount(async () => {
      loading.value = true
      await leads.value.refresh().catch((error) => {
        alert(error)
      })
      triggerRef(leads)
      leadRefs.value = leads.value.list
      loading.value = false
    })

    const getLeads = async () => {
      updatingTable.value = true
      setFilters()

      await leads.value.refresh().catch((error) => {
        alert(error)
      })
      triggerRef(leads)
      updatingTable.value = false
    }

    const formatDate = (date) => {
      return new Date(date)
    }

    const formatDateString = (date) => {
      return formatDate(date).toLocaleDateString()
    }

    const formatDateQueryParam = (date) => {
      return formatDate(date).toISOString().split('T')[0]
    }

    const filterByCampaign = (option) => {
      if (option) {
        let selectedCampaign = campaignFilters.value.find((campaign) => campaign.id == option)
        selectedCampaignFilter.name = selectedCampaign.name
        selectedCampaignFilter.location = selectedCampaign.location
        selectedCampaignFilter.id = selectedCampaign.id
        getLeads()
      } else {
        clearCampaignFilter()
      }
    }

    const filterByFundraiser = (option) => {
      if (option) {
        let selectedFundraiser = fundraiserFilters.value.find(
          (fundraiser) => fundraiser.id == option,
        )
        selectedFundraiserFilter.name = selectedFundraiser.fullName
        selectedFundraiserFilter.id = selectedFundraiser.id
        getLeads()
      } else {
        clearFundraiserFilter()
      }
    }

    const filterByDate = () => {
      if (
        (selectedDateRange.date1 && selectedDateRange.date2) ||
        (!selectedDateRange.date1 && !selectedDateRange.date)
      ) {
        getLeads()
      }
    }

    const getDateRangeOption1 = (date) => {
      if (date) {
        // eslint-disable-next-line
        date = date.replace(/-/g, '\/')
        selectedDateRange.date1 = formatDateQueryParam(date)
      } else {
        selectedDateRange.date1 = ''
      }
      filterByDate()
    }

    const getDateRangeOption2 = (date) => {
      if (date) {
        // eslint-disable-next-line
        date = date.replace(/-/g, '\/')
        selectedDateRange.date2 = formatDateQueryParam(date)
      } else {
        selectedDateRange.date2 = ''
      }
      filterByDate()
    }

    const getStartAndEndDates = () => {
      let date1 = selectedDateRange.date1
      let date2 = selectedDateRange.date2

      if (date1 && date2) {
        if (date1 >= date2) {
          leadFilters.dateRangeStart = date2
          leadFilters.dateRangeEnd = date1
        } else if (date1 <= date2) {
          leadFilters.dateRangeStart = date1
          leadFilters.dateRangeEnd = date2
        }
      } else if (!date1 || !date2) {
        leadFilters.dateRangeStart = leadFilters.dateRangeEnd = ''
      }
    }

    const clearCampaignFilter = () => {
      Object.keys(selectedCampaignFilter).map((key) => (selectedCampaignFilter[key] = ''))
      getLeads()
    }

    const clearFundraiserFilter = () => {
      Object.keys(selectedFundraiserFilter).map((key) => (selectedFundraiserFilter[key] = ''))
      getLeads()
    }

    const setFilters = () => {
      leadFilters.campaign = selectedCampaignFilter.id
      leadFilters.fundraiser = selectedFundraiserFilter.id
      getStartAndEndDates()
    }

    const exportLeads = () => {
      setFilters()
      const { ordering, ...filters } = leadFilters
      LeadApi.csc.generateExport({ ...filters })
    }

    return {
      isGivarooStaff,
      leads,
      leadFilters,
      loading,
      updatingTable,
      campaignFilters,
      fundraiserFilters,
      selectedCampaignFilter,
      selectedFundraiserFilter,
      selectedDateRange,
      filterByCampaign,
      filterByFundraiser,
      formatDateString,
      getDateRangeOption1,
      getDateRangeOption2,
      clearCampaignFilter,
      clearFundraiserFilter,
      exportLeads,
      isNPOAdmin,
      isCanvassingOrgAdmin,
      isCanvassingOrgFieldDirector,
      showFilterDrawer,
      canSeeDetails,
      activeFilterCount,
      leadColumns,
      FunnelIcon,
      ArrowDownTrayIcon,
    }
  },
}
</script>
